// PO 2026-02 · Post-onboarding add-trade discoverability CTA.
// Renders on the Dashboard for contractors who have already completed
// initial trade setup. Deep-links into the existing Set Up Your
// Business → Business & Trades step so contractors can toggle a new
// trade using the SAME UI they used during onboarding. No new trade
// management workflow, no duplicate trade UI. Existing trades,
// pricing rules, cohorts, projects, estimates, Levi context, and
// sent documents are preserved by the existing reconciliation logic
// in server.py:PUT /company-profile.
//
// Visibility rule: the card only renders once the contractor has at
// least one trade selected AND their SoyB Step 1 is complete on the
// server. This is the exact opposite of SetUpYourBusinessCard, so
// there is never a screen where BOTH cards compete for the same CTA.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PlusCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const DEEP_LINK = "/app/setup?step=business_and_trades";

const AddAnotherTradeCard = () => {
  const { t } = useLang();
  const [state, setState] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/set-up-your-business/state"),
      api.get("/company-profile"),
    ])
      .then(([sR, pR]) => {
        if (cancelled) return;
        setState(sR.data);
        setProfile(pR.data);
      })
      .catch(() => { /* silent — card simply hides on error */ });
    return () => { cancelled = true; };
  }, []);

  if (!state || !profile) return null;
  const step1Complete = !!state?.steps?.[0]?.complete;
  const trades = Array.isArray(profile?.trade_categories)
    ? profile.trade_categories : [];
  // Only surface once the contractor is past initial trade selection.
  if (!step1Complete || trades.length === 0) return null;

  return (
    <section
      data-testid="add-another-trade-card"
      data-trade-count={trades.length}
      className="jc-card p-4 mb-6 flex items-center justify-between gap-3 border-l-4 border-l-emerald-500"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <PlusCircle size={18} />
        </div>
        <div className="min-w-0">
          <div
            className="text-sm font-semibold text-[var(--jc-text)]"
            data-testid="add-another-trade-title"
          >
            {t("soyb.add_another_trade")}
          </div>
          <div
            className="text-xs text-[var(--jc-text-soft)]"
            data-testid="add-another-trade-hint"
          >
            {t("soyb.add_another_trade_hint")}
          </div>
        </div>
      </div>
      <Link
        to={DEEP_LINK}
        aria-label={t("soyb.add_another_trade_cta_aria")}
        data-testid="add-another-trade-cta"
        data-deep-link={DEEP_LINK}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
      >
        {t("soyb.add_another_trade")} <ArrowUpRight size={12} />
      </Link>
    </section>
  );
};

export default AddAnotherTradeCard;
